import React, { useEffect, useState } from "react";
import { User, LogOut, Settings } from "lucide-react";
import PremiumHeader from "../components/ui/PremiumHeader";
import MembershipStatsCard from "../components/ui/MembershipStatsCard";
import HistoryEventCard from "../components/ui/HistoryEventCard";
import MatchHistoryDetailModal from "../components/modals/MatchHistoryDetailModal";
import { useAuth } from "../hooks";
import { useMypageContext } from "../context/MypageContext";
import { UI_TEXT } from "../constants/uiText";
import type {
  MypageParticipatedEvent,
  MypageParticipatedMatchResult,
} from "../types/swagger";
import { getJstYearLabel } from "../utils/eventSections";

interface ProfileViewProps {
  onLogout: () => void;
  onNotificationClick: () => void;
  onSettingsClick: () => void;
}

type FlatEvent = {
  event_name: string;
  event_date: string;
  singles_rank: number;
  singles_point: number;
  event_group_name?: string;
  match_results: MypageParticipatedMatchResult[];
};

const flattenParticipatedEvents = (
  participated: MypageParticipatedEvent[],
): FlatEvent[] =>
  participated.flatMap(({ event_group, events }) =>
    events.map((e) => ({
      ...e,
      event_group_name: event_group?.name,
      match_results: e.match_results ?? [],
    })),
  );

const groupEventsByYear = (events: FlatEvent[]) => {
  const sorted = [...events].sort(
    (left, right) =>
      new Date(right.event_date ?? "").getTime() -
      new Date(left.event_date ?? "").getTime(),
  );

  return sorted.reduce<Array<{ year: string; events: FlatEvent[] }>>(
    (groups, event) => {
      const year = getJstYearLabel(event.event_date ?? "");
      const currentGroup = groups[groups.length - 1];

      if (!currentGroup || currentGroup.year !== year) {
        groups.push({ year, events: [event] });
        return groups;
      }

      currentGroup.events.push(event);
      return groups;
    },
    [],
  );
};

const ProfileView: React.FC<ProfileViewProps> = ({
  onLogout,
  onNotificationClick,
  onSettingsClick,
}) => {
  const { mypage, isLoading, error, refresh } = useMypageContext();
  const { logout } = useAuth();
  const [selectedEvent, setSelectedEvent] = useState<FlatEvent | null>(null);

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const eventHistoryByYear = groupEventsByYear(
    flattenParticipatedEvents(mypage?.participated_events ?? []),
  );

  const handleLogout = async () => {
    await logout();
    onLogout();
  };

  return (
    <div className="flex flex-col h-full animate-in fade-in duration-300 overflow-hidden">
      <div className="overflow-y-auto scrollbar-hide flex-1">
        {" "}
        <PremiumHeader
          title={UI_TEXT.profile.title}
          icon={User}
          theme="emerald"
          onNotificationClick={onNotificationClick}
        />
        <div className="px-6 py-6 pb-40 text-center">
          <MembershipStatsCard
            points={mypage?.yearly_point ?? "-"}
            rank={mypage?.yearly_rank ?? "-"}
            dartsliveName={mypage?.dartslive_card_name}
            eventGroupName={mypage?.event_group?.name}
            theme="emerald"
          />
          <button
            onClick={onSettingsClick}
            className="w-full bg-white border border-slate-200 text-slate-600 font-bold tracking-wider uppercase rounded-xl py-4 flex items-center justify-center gap-2 shadow-sm active:scale-95 mb-3 transition-all hover:bg-emerald-50 hover:text-emerald-600"
          >
            <Settings className="w-5 h-5" /> {UI_TEXT.profile.settings}
          </button>
          <button
            onClick={handleLogout}
            className="w-full bg-white border border-slate-200 text-slate-600 font-bold tracking-wider uppercase rounded-xl py-4 flex items-center justify-center gap-2 shadow-sm active:scale-95 mb-10 transition-all hover:bg-rose-50 hover:text-rose-600"
          >
            <LogOut className="w-5 h-5" /> {UI_TEXT.profile.logout}
          </button>
          <h2 className="text-[11px] font-black text-slate-400 uppercase tracking-widest mb-4 pl-1 text-left">
            {UI_TEXT.profile.matchHistory}
          </h2>
          {isLoading && (
            <p className="text-slate-400 text-xs font-bold text-center py-8">
              {UI_TEXT.common.loading}
            </p>
          )}
          {error && (
            <p className="text-rose-500 text-xs font-bold text-center py-8">
              {error}
            </p>
          )}
          {!isLoading &&
            !error &&
            eventHistoryByYear.map(({ year, events }) => (
              <section key={year} className="mb-8 text-left last:mb-0">
                <h3 className="text-[11px] font-black text-slate-400 uppercase tracking-widest mb-4 pl-1">
                  {`${year}`}
                </h3>
                {events.map((event, index) => (
                  <HistoryEventCard
                    key={`${year}-${event.event_name}-${event.event_date}-${index}`}
                    title={event.event_name ?? ""}
                    date={event.event_date ?? ""}
                    rank={event.singles_rank ?? 0}
                    points={event.singles_point ?? 0}
                    eventGroupName={event.event_group_name}
                    onClick={() => setSelectedEvent(event)}
                  />
                ))}
              </section>
            ))}
        </div>{" "}
      </div>{" "}
      <MatchHistoryDetailModal
        isOpen={selectedEvent !== null}
        onClose={() => setSelectedEvent(null)}
        eventName={selectedEvent?.event_name ?? ""}
        eventDate={selectedEvent?.event_date ?? ""}
        singlesRank={selectedEvent?.singles_rank ?? 0}
        singlesPoint={selectedEvent?.singles_point ?? 0}
        matchResults={selectedEvent?.match_results ?? []}
      />
    </div>
  );
};

export default ProfileView;
